import React, { FC, useMemo } from 'react';
import FormControl from '@mui/material/FormControl';
import { makeStyles } from '@mui/styles';
import cx from 'classnames';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import { BusinessPicker } from 'components/common/businessPicker/BusinessPicker';
import { useLightweightStoresInBusiness } from 'api/queries/useLightweightStoresInBusiness';

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: 200,
  },
  select: {
    height: '50px',
    width: '205px',
  },
}));

export interface BusinessAndStoreEditFormProps {
  businessId: number;
  storeId: number;
  updatedBusinessId: number;
  updatedStoreId: number;
  handleBusinessChange: (businessId: number | null) => void | Promise<void>;
  handleStoreChange: (e: any) => void | Promise<void>;
  onSave: () => void | Promise<void>;
  error?: string | null;
}

export const BusinessAndStoreEditForm: FC<BusinessAndStoreEditFormProps> = ({
  businessId,
  updatedBusinessId,
  handleBusinessChange,
  storeId,
  updatedStoreId,
  handleStoreChange,
  onSave,
  error,
}) => {
  const materialClasses = useStyles();

  const isSaveDisabled = Boolean(
    (updatedBusinessId && !updatedStoreId) || (!updatedBusinessId && updatedStoreId),
  );

  const { data: stores } = useLightweightStoresInBusiness(updatedBusinessId);

  const storesOptions = useMemo(() => {
    return stores?.map(({ id, name }) => ({ value: id, display: name })) ?? [];
  }, [stores]);

  return (
    <div className="individual-value-edit-form-container">
      <h1 className="individual-value-edit-form-content-header">Edit Business and Store</h1>
      <div className="individual-value-edit-form-content">
        <div className="individual-value-edit-form-content-row">
          <h1 className="individual-value-edit-form-content-row-label">Current Business</h1>
          <p className={'individual-value-edit-form-content-input'}>{businessId || '--'}</p>
        </div>
        <div className="individual-value-edit-form-content-row">
          <h1 className="individual-value-edit-form-content-row-label">Updated Business</h1>
          <div className="individual-value-edit-form-content-input">
            <FormControl variant="outlined" className={materialClasses.formControl}>
              <BusinessPicker
                id="business-and-store-edit-business"
                selectedBusinessId={updatedBusinessId}
                onChange={handleBusinessChange}
                label="Business"
              />
            </FormControl>
          </div>
        </div>
      </div>
      <div className="individual-value-edit-form-content">
        <div className="individual-value-edit-form-content-row">
          <h1 className="individual-value-edit-form-content-row-label">Current Store</h1>
          <p className={'individual-value-edit-form-content-input'}>{storeId || '--'}</p>
        </div>
        <div className="individual-value-edit-form-content-row">
          <h1 className="individual-value-edit-form-content-row-label">Updated Store</h1>
          <div className="individual-value-edit-form-content-input">
            <FormControl variant="outlined" className={materialClasses.formControl}>
              <AutocompleteDropdown
                key={updatedBusinessId}
                id="individual-value-edit-select"
                selectedData={updatedStoreId}
                onChange={handleStoreChange}
                label="Store"
                selectData={storesOptions}
              />
            </FormControl>
          </div>
        </div>
      </div>
      <div className="individual-value-edit-form-content">
        {error && <p className="error-text">{error}</p>}
        <button
          className={cx('individual-value-edit-submit-button', isSaveDisabled && 'disabled')}
          onClick={onSave}
          disabled={isSaveDisabled}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};
